import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import 'highlight.js/styles/github.css';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';

import { logger } from '@core/utils';

/**
 * Module-level queue to serialize mermaid renders.
 * Mermaid uses global state and breaks when two diagrams render concurrently.
 */
let renderQueue = Promise.resolve();

/**
 * Zoomable wrapper with "Click to expand" hint.
 */
function ZoomableBlock({ children }) {
  const { t } = useTranslation();
  // `relative` is load-bearing: react-medium-image-zoom places an absolute
  // "ghost" over the image at the image's offsetTop. With no positioned
  // ancestor that offset is measured from <body>, so a chart low in a long
  // chat sat far below the viewport, grew the document and scrolling moved
  // the whole app up into a blank page (2026-09-21).
  return (
    <figure className="my-4 max-w-md mx-auto">
      <div className="relative border border-gray-200 rounded-lg overflow-hidden">
        <Zoom>{children}</Zoom>
      </div>
      <figcaption className="text-xs text-gray-400 mt-1.5 text-center">
        {t('core.markdown.clickToExpand')}
      </figcaption>
    </figure>
  );
}

ZoomableBlock.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Strip HTML tags that LLMs sometimes inject into mermaid source.
 * Replace <br/> with newline so labels still break correctly.
 */
function sanitizeMermaidSource(src) {
  let clean = src.replace(/<br\s*\/?>/gi, '\n');
  clean = clean.replace(/<\/?[a-z][^>]*>/gi, '');
  return clean.trim();
}

/**
 * Renders a Mermaid diagram from source text. Click to expand fullscreen.
 */
function MermaidDiagram({ chart }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [svgHtml, setSvgHtml] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const renderDiagram = useCallback(() => {
    if (!chart) return;

    const cleanChart = sanitizeMermaidSource(chart);
    setError(null);
    setSvgHtml(null);

    // Serialize renders through a module-level queue to avoid mermaid global state conflicts
    renderQueue = renderQueue
      .then(async () => {
        if (!containerRef.current) return;

        const mermaid = (await import('mermaid')).default;
        mermaid.initialize({
          startOnLoad: false,
          theme: 'default',
          securityLevel: 'loose',
        });

        // Use mermaid.run() with a pre-populated node instead of mermaid.render()
        // to avoid the "firstChild is null" bug caused by orphaned temp DOM elements
        const node = document.createElement('pre');
        node.className = 'mermaid';
        node.textContent = cleanChart;
        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(node);

        try {
          await mermaid.run({ nodes: [node] });
        } catch (err) {
          // mermaid.run() sometimes throws after partially rendering (e.g. getBoundingClientRect
          // on text measurement). If an SVG was produced, treat as success.
          if (!containerRef.current?.querySelector('svg')) {
            logger.error('Mermaid render error:', err);
            setError('Failed to render diagram');
            return;
          }
        }

        // Detect mermaid error SVGs (contain "Syntax error" text)
        const svg = containerRef.current?.querySelector('svg');
        if (svg?.textContent?.includes('Syntax error')) {
          setError('Failed to render diagram');
          return;
        }

        if (svg) {
          svg.style.background = '#ffffff';
          for (const rect of svg.querySelectorAll(':scope > rect')) {
            rect.setAttribute('fill', '#ffffff');
          }
          // Override mermaid's dark fills for the fullscreen overlay.
          // The root SVG selector sets fill:#333 which paints the SVG canvas dark.
          // Section-root/section--1 use dark backgrounds for timeline frames.
          const svgId = svg.getAttribute('id');
          const style = svg.querySelector('style');
          if (style && svgId) {
            style.textContent += `
              #${svgId} { fill: transparent !important; }
              #${svgId} text { fill: #333 !important; }
              #${svgId} .section-root rect,
              #${svgId} .section-root path,
              #${svgId} .section-root circle,
              #${svgId} .section--1 path,
              #${svgId} .section--1 rect { fill: #f3f4f6 !important; }
              #${svgId} .section--1 text,
              #${svgId} .section-root text { fill: #333 !important; }
            `;
          }
          // Capture the SVG HTML for the fullscreen overlay.
          // Remove mermaid's max-width so the SVG fills the overlay naturally.
          svg.style.removeProperty('max-width');
          setSvgHtml(containerRef.current.innerHTML);
          // Restore original styles for the inline view
          svg.style.maxWidth = '';
          if (style) {
            style.textContent = style.textContent.replace(/\[id\^="mermaid"\].*!important;\s*\}/g, '');
          }
        }
      })
      .catch(() => {});
  }, [chart]);

  useEffect(() => {
    renderDiagram();
  }, [renderDiagram]);

  // Close on Escape key
  useEffect(() => {
    if (!expanded) return;
    const handleKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [expanded]);

  if (error) {
    return (
      <div className="my-4">
        <p className="text-xs text-gray-400 mb-1">{t('core.markdown.diagramSource')}</p>
        <pre className="bg-[var(--color-bg-subtle)] border border-[var(--color-border)] rounded-lg p-4 overflow-x-auto text-sm text-[var(--color-text)]">
          <code>{chart}</code>
        </pre>
      </div>
    );
  }

  return (
    <>
      <figure className="my-4 max-w-md mx-auto">
        <div
          className="border border-gray-200 rounded-lg overflow-hidden cursor-pointer"
          onClick={() => svgHtml && setExpanded(true)}
        >
          <div
            ref={containerRef}
            className="not-prose flex justify-center overflow-x-auto bg-white rounded-lg"
          />
        </div>
        <figcaption className="text-xs text-gray-400 mt-1.5 text-center">
          {t('core.markdown.clickToExpand')}
        </figcaption>
      </figure>

      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 cursor-pointer"
          onClick={() => setExpanded(false)}
        >
          <div
            className="relative w-[90vw] max-h-[90vh] flex flex-col bg-white rounded-lg cursor-default"
            role="dialog"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Escape') setExpanded(false); }}
          >
            <button
              type="button"
              className="absolute top-2 right-2 z-10 rounded-full bg-gray-100 p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
              onClick={() => setExpanded(false)}
              aria-label={t('core.markdown.close')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <div
              className="overflow-auto p-6 [&_pre]:!bg-white [&_pre]:!border-0 [&_pre]:!p-0 [&_svg]:max-w-full [&_svg]:max-h-[calc(90vh-3rem)] [&_svg]:h-auto [&_svg]:mx-auto"
              dangerouslySetInnerHTML={{ __html: svgHtml }}
            />
          </div>
        </div>
      )}
    </>
  );
}

MermaidDiagram.propTypes = {
  chart: PropTypes.string.isRequired,
};

/**
 * Split markdown content into text segments and mermaid blocks.
 * Returns an array of { type: 'md' | 'mermaid', content: string }.
 */
function splitMermaidBlocks(md) {
  if (!md) return [{ type: 'md', content: '' }];
  const parts = [];
  const regex = /```mermaid\s*\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(md)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'md', content: md.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'mermaid', content: match[1] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < md.length) {
    parts.push({ type: 'md', content: md.slice(lastIndex) });
  }
  return parts.length ? parts : [{ type: 'md', content: md }];
}

// The agent announces every saved page as "[[page-name]]". That rendered as
// plain text, so users were told a page existed and had no way to reach it.
const WIKILINK_RE = /\[\[([^[\]|\n]{1,120})\]\]/g;
// Split on fenced blocks and inline code so a wikilink inside a code sample
// stays literal.
const CODE_RE = /(```[\s\S]*?```|`[^`\n]*`)/g;

function linkWikilinks(markdown) {
  if (!markdown?.includes('[[')) return markdown;
  return markdown
    .split(CODE_RE)
    .map((chunk, index) => (index % 2 === 1
      ? chunk
      : chunk.replace(WIKILINK_RE, (_match, name) => {
        const page = name.trim();
        return `[${page}](/settings/knowledge-base?page=${encodeURIComponent(page)})`;
      })))
    .join('');
}

/**
 * MarkdownViewer renders markdown content with GFM, syntax highlighting, and Mermaid diagrams.
 */
export function MarkdownViewer({ content, className = '', components: customComponents, generatedFiles }) {
  const { t } = useTranslation();
  // Preprocess content: replace non-HTTP image URLs with real URLs from generated_files.
  // The LLM often hallucinates sandbox:/output/file.png instead of using the real URL.
  const resolvedContent = useMemo(() => {
    const linked = linkWikilinks(content);
    if (!generatedFiles?.length || !linked) return linked;
    return linked.replace(/!\[([^\]]*)\]\((?!https?:\/\/)([^)]+)\)/g, (match, alt, src) => {
      const basename = src.split('/').pop();
      const file = generatedFiles.find((f) => f.filename === basename);
      return file ? `![${alt}](${file.url})` : match;
    });
  }, [content, generatedFiles]);

  const defaultComponents = {
    img({ src, alt, ...props }) {
      const filename = src ? src.split('/').pop().split('?')[0] : 'image';
      return (
        <figure className="my-4 max-w-md mx-auto">
          {/* `relative`: see ZoomableBlock, the zoom ghost needs a positioned parent. */}
          <div className="relative border border-gray-200 rounded-lg overflow-hidden">
            <Zoom>
              <img src={src} alt={alt} className="rounded-lg max-w-full" {...props} />
            </Zoom>
          </div>
          <div className="flex items-center justify-center gap-3 mt-1.5">
            <span className="text-xs text-gray-400">{t('core.markdown.clickToExpand')}</span>
            <a
              href={src}
              download={filename}
              className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              {t('core.markdown.download')}
            </a>
          </div>
        </figure>
      );
    },
    pre({ children, ...props }) {
      return (
        <pre className="bg-[var(--color-bg-subtle)] border border-[var(--color-border)] rounded-lg p-4 overflow-x-auto text-[var(--color-text)] text-sm" {...props}>
          {children}
        </pre>
      );
    },
    code({ className: codeClassName, children, ...props }) {
      if (!codeClassName) {
        return (
          <code className="px-1.5 py-0.5 bg-[var(--color-bg-muted)] rounded text-sm" {...props}>
            {children}
          </code>
        );
      }

      return (
        <code className={codeClassName} {...props}>
          {children}
        </code>
      );
    },
    ...customComponents,
  };

  const parts = splitMermaidBlocks(resolvedContent);

  return (
    <div className={`prose prose-sm max-w-none ${className}`}>
      {parts.map((part, i) =>
        part.type === 'mermaid' ? (
          <MermaidDiagram key={i} chart={part.content} />
        ) : (
          <ReactMarkdown
            key={i}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight]}
            urlTransform={(url) =>
              url?.startsWith('upload://') ? url : defaultUrlTransform(url)
            }
            components={defaultComponents}
          >
            {part.content}
          </ReactMarkdown>
        ),
      )}
    </div>
  );
}

MarkdownViewer.propTypes = {
  content: PropTypes.string,
  className: PropTypes.string,
  components: PropTypes.object,
};
